/* Ardor designers: "Artwork by link" + "Email me the proof to approve" (assets/ardor-art-link.js).
   New Oct 19, 2026 (Claude ART0, approved by Dayne). Loaded (defer) by snippets/ardor-art-link.liquid, which a designer
   section renders once, after its own scripts. Plain JS, no third-party code. It makes no network request until a buyer
   pastes a link or the page is opened with ?art=.

   1) Artwork by link. Adds "Or paste a link to your artwork" next to the designer's upload control and reads
      ?art=<url-encoded https link> on load. The browser fetches the file (works when the host allows cross-site reads:
      Shopify CDN, Dropbox direct links, GitHub raw, Imgur and most image CDNs) and hands it to the designer's own file
      input, so it takes exactly the same path as a normal upload (preview, proof, print files, cart).
      When the host blocks it (Google Drive, share pages, expired links) the link is kept: the designer adds the line item
      property "Artwork link" and switches that order to an emailed proof ("Ardor will build the proof from your link").
   2) Email proof. Adds a choice to the designer's proof dialog. "Email me the proof to approve" swaps the name, phone,
      signature and color box for the buyer's email (required) and the note "Production starts after you approve the
      proof by email". The designer then adds "Proof approval: Email - pending" and "Proof email: <email>".
      Nothing is emailed from here (Shopify Flow handles notifications).

   Designer hooks (a few guarded lines per designer; see SPEC-ART.md):
     ArdorArtLink.link(root, currentFile or [files]) -> { url, loaded } | null   (the link behind the current artwork)
     ArdorArtLink.emailChosen(root)       -> true when the buyer picked "Email me the proof to approve"
     ArdorArtLink.proofEmail(root)        -> the validated email, or '' (and shows the error in the dialog)
   For browser agents: ArdorArtLink.importUrl('https://...') -> Promise<{ ok, mode: 'loaded'|'link'|'error', message }>
*/
(function () {
  'use strict';
  if (window.ArdorArtLink) return;
  var W = window, D = document;
  var MIME = { png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', webp: 'image/webp', gif: 'image/gif', svg: 'image/svg+xml', pdf: 'application/pdf', ai: 'application/postscript', eps: 'application/postscript', tif: 'image/tiff', tiff: 'image/tiff', psd: 'image/vnd.adobe.photoshop' };
  var EXT = { 'image/png': 'png', 'image/jpeg': 'jpg', 'image/jpg': 'jpg', 'image/pjpeg': 'jpg', 'image/webp': 'webp', 'image/gif': 'gif', 'image/svg+xml': 'svg', 'application/pdf': 'pdf', 'application/x-pdf': 'pdf', 'application/postscript': 'eps', 'application/eps': 'eps', 'application/x-eps': 'eps', 'image/eps': 'eps', 'image/x-eps': 'eps', 'application/illustrator': 'ai', 'application/vnd.adobe.illustrator': 'ai', 'image/tiff': 'tif', 'image/vnd.adobe.photoshop': 'psd', 'application/x-photoshop': 'psd', 'application/photoshop': 'psd' };
  var BAD = /^(docx?|xlsx?|pptx?|pages|key|numbers|txt|rtf|csv|zip|rar|7z|gz|mp4|mov|avi|mkv|webm|mp3|wav|m4a|exe|dmg|apk|heic|heif|cdr|indd)$/;
  var states = [], uid = 0;

  function readConf(el) { try { return JSON.parse(el.textContent || '{}'); } catch (e) { return null; } }
  function q(sel, el) { try { return sel ? (el || D).querySelector(sel) : null; } catch (e) { return null; } }
  function qa(sel, el) { try { return sel ? Array.prototype.slice.call((el || D).querySelectorAll(sel)) : []; } catch (e) { return []; } }
  function stateFor(root) { for (var i = 0; i < states.length; i++) if (states[i].root === root) return states[i]; return null; }
  function mb(n) { return n < 1048576 ? Math.max(1, Math.round(n / 1024)) + ' KB' : (n / 1048576).toFixed(n < 10485760 ? 1 : 0) + ' MB'; }
  function extOf(name) { var m = /\.([a-z0-9]{2,5})$/i.exec(String(name || '')); return m ? m[1].toLowerCase() : ''; }
  function same(a, b) { return !!a && !!b && (a === b || (a.name === b.name && a.size === b.size && a.lastModified === b.lastModified)); }

  // Share links that have a direct-download twin the browser is allowed to read.
  function direct(u) {
    try {
      var x = new URL(u);
      if (/(^|\.)dropbox\.com$/i.test(x.hostname) && /^\/(s|scl)\//.test(x.pathname)) {
        x.hostname = 'dl.dropboxusercontent.com'; x.searchParams.delete('dl'); x.searchParams.delete('raw');
        return x.toString();
      }
      var g = /^\/([^/]+)\/([^/]+)\/blob\/(.+)$/.exec(x.pathname);
      if (x.hostname === 'github.com' && g) return 'https://raw.githubusercontent.com/' + g[1] + '/' + g[2] + '/' + g[3];
    } catch (e) {}
    return u;
  }

  function checkUrl(st, raw) {
    var u = String(raw || '').trim(), x;
    if (!u) return { error: '' };
    if (u.length > 2000) return { error: 'That link is too long. Paste the direct link to the file.' };
    try { x = new URL(u); } catch (e) { return { error: 'That doesn\'t look like a link. Paste a full link that starts with https://' }; }
    if (x.protocol !== 'https:') return { error: 'Use a secure link that starts with https://' };
    if (x.username || x.password) return { error: 'Use a link without a user name or password in it.' };
    var ext = ''; try { ext = extOf(decodeURIComponent(x.pathname)); } catch (e) { ext = extOf(x.pathname); }
    if (ext && (BAD.test(ext) || (MIME[ext] && st.types.indexOf(ext) < 0))) {
      return { error: 'That link is a .' + ext + ' file. Use ' + st.note + '.' };
    }
    return { url: x.toString(), ext: MIME[ext] ? (ext === 'jpeg' ? 'jpg' : ext === 'tiff' ? 'tif' : ext) : '' };
  }

  function sniff(blob) {
    if (!blob.slice || !blob.slice(0, 1).arrayBuffer) return Promise.resolve('');
    return blob.slice(0, 512).arrayBuffer().then(function (buf) {
      var b = new Uint8Array(buf), s = '';
      for (var i = 0; i < b.length; i++) s += String.fromCharCode(b[i]);
      if (s.indexOf('%PDF') === 0) return 'pdf';
      if (b[0] === 0x89 && s.substr(1, 3) === 'PNG') return 'png';
      if (b[0] === 0xFF && b[1] === 0xD8 && b[2] === 0xFF) return 'jpg';
      if (s.indexOf('GIF8') === 0) return 'gif';
      if (s.indexOf('RIFF') === 0 && s.substr(8, 4) === 'WEBP') return 'webp';
      if (s.indexOf('%!PS') === 0 || (b[0] === 0xC5 && b[1] === 0xD0 && b[2] === 0xD3 && b[3] === 0xC6)) return 'eps';
      if (s.indexOf('8BPS') === 0) return 'psd';
      if (s.indexOf('II*') === 0 || s.indexOf('MM\u0000*') === 0) return 'tif';
      if (/<svg[\s>]/i.test(s)) return 'svg';
      if (/<!doctype html|<html[\s>]/i.test(s)) return 'html';
      return '';
    }).catch(function () { return ''; });
  }

  function fileName(url, ext) {
    var base = '';
    try { base = decodeURIComponent(new URL(url).pathname.split('/').pop() || ''); } catch (e) {}
    base = base.replace(/[^\w .()+-]+/g, '_').replace(/^[ ._]+/, '').slice(0, 80) || 'artwork-from-link';
    var have = extOf(base); have = have === 'jpeg' ? 'jpg' : have === 'tiff' ? 'tif' : have;
    return have === ext ? base : base.replace(/\.[a-z0-9]{2,5}$/i, '') + '.' + ext;
  }

  // Fetch the file in the browser. Resolves { file } | { blocked: true } | { error }.
  function getFile(st, chk) {
    var url = direct(chk.url), ctl = W.AbortController ? new AbortController() : null;
    var timer = ctl ? setTimeout(function () { ctl.abort(); }, 45000) : 0;
    var opts = { mode: 'cors', credentials: 'omit', redirect: 'follow', referrerPolicy: 'no-referrer', cache: 'no-store' };
    if (ctl) opts.signal = ctl.signal;
    return fetch(url, opts).then(function (r) {
      if (!r.ok) {
        if (r.status === 401 || r.status === 403) return { blocked: true };
        return { error: 'That link didn\'t work (error ' + r.status + '). Check the link, or upload the file instead.' };
      }
      var len = +(r.headers.get('content-length') || 0);
      if (len > st.max) return { error: 'That file is ' + mb(len) + '. The limit here is ' + mb(st.max) + '. Order with a smaller file, then email the full file to info@ardorprinting.com.' };
      var ctype = String(r.headers.get('content-type') || '').split(';')[0].trim().toLowerCase();
      return r.blob().then(function (blob) {
        if (blob.size > st.max) return { error: 'That file is ' + mb(blob.size) + '. The limit here is ' + mb(st.max) + '. Order with a smaller file, then email the full file to info@ardorprinting.com.' };
        if (!blob.size) return { error: 'That link returned an empty file. Check the link, or upload the file instead.' };
        return sniff(blob).then(function (sn) {
          if (sn === 'html' || ctype === 'text/html') return { blocked: true, page: true };
          // What the bytes say wins (an .ai file is PDF or PostScript inside), then the link, then the server's type.
          var ext = (chk.ext === 'ai' && (sn === 'pdf' || sn === 'eps')) ? 'ai' : (sn || chk.ext || EXT[ctype] || '');
          if (!ext || st.types.indexOf(ext) < 0) {
            return { error: 'That link isn\'t an artwork file we can use. Use ' + st.note + '.' };
          }
          var type = MIME[ext] || blob.type || 'application/octet-stream';
          var f = new File([blob], fileName(chk.url, ext), { type: type, lastModified: Date.now() });
          return { file: f };
        });
      });
    }).catch(function () { return { blocked: true }; }).then(function (res) { if (timer) clearTimeout(timer); return res; });
  }

  // Hand the file to the designer's own upload path (its file input), like a buyer picking it.
  function handOff(st, file) {
    var inp = q(st.c.input, st.root);
    if (inp && W.DataTransfer) {
      try { var dt = new DataTransfer(); dt.items.add(file); inp.files = dt.files; inp.dispatchEvent(new Event('change', { bubbles: true })); return true; } catch (e) {}
    }
    var api = st.c.api && W[st.c.api];
    if (api && typeof api.loadFile === 'function') { try { api.loadFile(file, 'front', true); return true; } catch (e) {} }
    return false;
  }

  function say(st, text, kind, canRemove) {
    st.ui.forEach(function (u) {
      u.msg.textContent = text || '';
      u.msg.hidden = !text;
      u.msg.className = 'aal__msg' + (kind ? ' aal__msg--' + kind : '');
      if (text && canRemove) {
        var b = D.createElement('button'); b.type = 'button'; b.className = 'aal__x'; b.textContent = 'Remove link';
        b.addEventListener('click', function () { clear(st); });
        u.msg.appendChild(D.createTextNode(' ')); u.msg.appendChild(b);
      }
    });
  }
  function busy(st, on) { st.busy = on; st.ui.forEach(function (u) { u.btn.disabled = on; u.btn.textContent = on ? 'Getting it…' : 'Use link'; }); }
  function clear(st) { st.url = ''; st.file = null; st.mode = ''; st.ui.forEach(function (u) { u.input.value = ''; }); say(st, ''); }

  function importUrl(st, raw) {
    if (st.busy) return Promise.resolve({ ok: false, mode: 'error', message: 'Still getting the last link.' });
    var chk = checkUrl(st, raw);
    if (chk.error === '') { clear(st); return Promise.resolve({ ok: false, mode: 'error', message: 'No link.' }); }
    if (chk.error) { st.url = ''; st.file = null; st.mode = ''; say(st, chk.error, 'error'); return Promise.resolve({ ok: false, mode: 'error', message: chk.error }); }
    st.ui.forEach(function (u) { u.input.value = chk.url; });
    busy(st, true); say(st, 'Getting your file from the link…', 'busy');
    return getFile(st, chk).then(function (res) {
      busy(st, false);
      var m;
      if (res.file) {
        st.url = chk.url; st.file = res.file; st.mode = 'loaded';
        if (!handOff(st, res.file)) { st.mode = 'link'; st.file = null; }
        else { m = 'Loaded from your link: ' + res.file.name + ' (' + mb(res.file.size) + ').'; say(st, m, 'ok', false); return { ok: true, mode: 'loaded', message: m }; }
      }
      if (res.error) { st.url = ''; st.file = null; st.mode = ''; say(st, res.error, 'error'); return { ok: false, mode: 'error', message: res.error }; }
      if (st.c.fallback === false) {
        // This designer needs the real file (its price, cut line or print file are made from it), so no link-only orders.
        st.url = ''; st.file = null; st.mode = ''; // Oct 19: a failed link must not leave an older link on the order
        m = 'We can\'t open that link from here. Download the file and upload it above, or email it to info@ardorprinting.com.';
        say(st, m, 'error'); return { ok: false, mode: 'error', message: m };
      }
      st.url = chk.url; st.file = null; st.mode = 'link';
      // The link replaces any art already on the design, so the preview doesn't show the wrong file.
      var api = st.c.api && W[st.c.api];
      if (api && api.getArt && api.setArt && api.getArt('front')) { try { api.setArt('front', null); } catch (e) {} }
      m = 'We can\'t open that link here, so your art won\'t show on the ' + st.noun + ' preview. You can still order: Ardor will build the proof from your link and email it to you to approve before we print.';
      say(st, m, 'link', true);
      return { ok: true, mode: 'link', message: m };
    });
  }

  function buildField(st, variant) {
    var id = 'aal-' + (++uid), box = D.createElement('div');
    box.className = 'aal aal--' + (variant || 'full');
    box.setAttribute('data-aal', '');
    var lab = D.createElement('label'); lab.className = 'aal__label'; lab.htmlFor = id;
    lab.textContent = 'Or paste a link to your artwork (' + st.note + ')';
    var row = D.createElement('div'); row.className = 'aal__row';
    var inp = D.createElement('input'); inp.id = id; inp.className = 'aal__input'; inp.type = 'url'; inp.inputMode = 'url';
    inp.placeholder = variant === 'compact' ? 'Or paste a link to your artwork' : 'https://';
    inp.autocomplete = 'off'; inp.spellcheck = false; inp.setAttribute('data-aal-input', '');
    var btn = D.createElement('button'); btn.type = 'button'; btn.className = 'aal__btn'; btn.textContent = 'Use link'; btn.setAttribute('data-aal-use', '');
    var msg = D.createElement('p'); msg.className = 'aal__msg'; msg.hidden = true; msg.setAttribute('role', 'status'); msg.setAttribute('aria-live', 'polite');
    row.appendChild(inp); row.appendChild(btn); box.appendChild(lab); box.appendChild(row); box.appendChild(msg);
    var go = function () { importUrl(st, inp.value); };
    btn.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); go(); });
    inp.addEventListener('keydown', function (e) { e.stopPropagation(); if (e.key === 'Enter') { e.preventDefault(); go(); } });
    ['click', 'pointerdown'].forEach(function (t) { box.addEventListener(t, function (e) { e.stopPropagation(); }); });
    st.ui.push({ box: box, input: inp, btn: btn, msg: msg });
    if (st.url) inp.value = st.url;
    return box;
  }

  function placeFields(st) {
    (st.c.anchors || []).forEach(function (a) {
      qa(a[0], st.root).forEach(function (el) {
        if (el.__aal) return; el.__aal = 1;
        var f = buildField(st, a[2]);
        if (a[1] === 'afterend' || a[1] === 'beforebegin') el.insertAdjacentElement(a[1], f); else { if (a[2] === 'compact' && W.getComputedStyle) { var cs = W.getComputedStyle(el); if (cs.overflowX === 'visible' && cs.overflowY === 'visible') { if (cs.position === 'static') el.style.position = 'relative'; f.className += ' aal--float'; } } el.insertAdjacentElement(a[1] || 'beforeend', f); } // Oct 19: float the compact field under a centered empty card so the card doesn't move (no layout shift)
      });
    });
  }

  // ---- email proof choice in the designer's proof dialog ----
  function formOf(st) { return q(st.c.form, st.root) || q(st.c.form); }
  function wrapOf(st, el) { return (el.closest && el.closest(st.c.wrap || 'label, .abb-field, .abb-check')) || el; }
  function setEmailMode(st, on) {
    st.email = !!on;
    var form = formOf(st); if (!form) return;
    qa(st.c.hide, form).forEach(function (el) { wrapOf(st, el).hidden = !!on; });
    if (st.noteEl) st.noteEl.hidden = !on;
    var sb = q(st.c.submit, form);
    if (sb) { if (!sb.__aalText) sb.__aalText = sb.textContent; sb.textContent = on ? 'Add to cart and email me the proof' : sb.__aalText; }
    var err = q(st.c.err, form); if (err) err.hidden = true;
    // Back to signing: re-run the designer's own signature setup so the (no longer hidden) pad is sized correctly.
    if (!on && st.c.refresh) { var sm = q(st.c.refresh, form); if (sm) try { sm.click(); } catch (e) {} }
  }
  function placeChoice(st) {
    if (st.c.proof === false || (st.choice && st.choice.isConnected)) return;
    var form = formOf(st), before = form && q(st.c.before, form);
    if (!form || !before) return;
    st.email = false;
    var n = ++uid, fs = D.createElement('fieldset');
    fs.className = 'aal-proof'; fs.setAttribute('data-aal-proof', '');
    fs.innerHTML = '<legend class="aal-proof__legend">How do you want to approve your proof?</legend>' +
      '<label class="aal-proof__opt"><input type="radio" name="aal-proof-' + n + '" value="sign" checked> <span>' + (W.ArdorProofLater ? 'Approve it now' : 'Sign it now') + '</span></label>' + // DSN
      '<label class="aal-proof__opt"><input type="radio" name="aal-proof-' + n + '" value="email"> <span>Email me the proof to approve</span></label>' +
      '<p class="aal-proof__note" hidden>Production starts after you approve the proof by email. We\'ll send it to ' + (W.ArdorProofLater ? 'the email you enter at checkout' : 'the email below') + '.</p>'; // DSN
    wrapOf(st, before).insertAdjacentElement('beforebegin', fs);
    st.choice = fs; st.noteEl = fs.querySelector('.aal-proof__note');
    qa('input[type=radio]', fs).forEach(function (r) { r.addEventListener('change', function () { if (r.checked) setEmailMode(st, r.value === 'email'); }); });
  }

  function addCss() {
    if (D.getElementById('aal-css')) return;
    var s = D.createElement('style'); s.id = 'aal-css';
    // Selectors are doubled / nested so the designers' own ".abb label", ".abb--app label" rules can't override them.
    s.textContent = '.aal.aal{margin:12px 0 0;text-align:left;font-size:13px;line-height:1.4}' +
      '.aal.aal--compact{margin:10px auto 0;max-width:340px}' +
      '.aal.aal--float{position:absolute;left:50%;top:100%;transform:translateX(-50%);width:340px;max-width:90vw;margin:10px 0 0;z-index:2}' +
      '.aal .aal__label{display:block;margin:0 0 6px;font-weight:600;font-size:13px}' +
      '.aal.aal--compact .aal__label{position:absolute;width:1px;height:1px;margin:0;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap}' +
      '.aal .aal__row{display:flex;gap:6px}' +
      '.aal .aal__input{flex:1 1 auto;min-width:0;height:38px;margin:0;padding:0 10px;border:1px solid #c9c9c9;border-radius:8px;background:#fff;color:#111;font-size:14px}' +
      '.aal .aal__input:focus{outline:2px solid #111;outline-offset:1px}' +
      '.aal .aal__btn{flex:0 0 auto;height:38px;margin:0;padding:0 14px;border:1px solid #111;border-radius:8px;background:#fff;color:#111;font-size:13px;font-weight:700;cursor:pointer}' +
      '.aal .aal__btn:disabled{opacity:.6;cursor:progress}' +
      '.aal .aal__msg{margin:6px 0 0;font-size:12.5px;color:#333}.aal .aal__msg--error{color:#b3261e}.aal .aal__msg--ok{color:#1d6b36}.aal .aal__msg--link{color:#5a4300}' +
      '.aal .aal__x{border:0;background:none;padding:0;margin:0;color:inherit;text-decoration:underline;font-size:inherit;cursor:pointer}' +
      '.aal-proof.aal-proof{display:block;border:1px solid #d6d6d6;border-radius:10px;padding:8px 12px 10px;margin:0 0 12px;min-width:0}' +
      '.aal-proof .aal-proof__legend{display:block;padding:0 4px;margin:0;font-weight:700;font-size:13px}' +
      '.aal-proof .aal-proof__opt{display:flex;align-items:center;gap:8px;margin:6px 0 0;font-size:14px;font-weight:500;cursor:pointer}' +
      '.aal-proof .aal-proof__opt input{margin:0;flex:0 0 auto}' +
      '.aal-proof .aal-proof__note{margin:8px 0 0;font-size:13px;font-weight:600;color:#5a4300}';
    D.head.appendChild(s);
  }

  function setup(confEl) {
    var c = readConf(confEl); if (!c) return;
    var root = q(c.root); if (!root || stateFor(root)) return;
    var types = String(c.types || 'png,jpg,jpeg,pdf,svg,ai,eps').toLowerCase().split(/[\s,]+/).filter(Boolean);
    var st = { root: root, c: c, ui: [], url: '', file: null, mode: '', busy: false, email: false,
      types: types, max: Math.max(1, +c.maxMb || 25) * 1048576, noun: c.noun || 'design', note: c.note || 'PNG, JPG, PDF, SVG, AI, EPS' };
    states.push(st);
    addCss(); placeFields(st); placeChoice(st);
    // A buyer's own upload or drop replaces a pasted link.
    root.addEventListener('change', function (e) { if (e.isTrusted && e.target && e.target.type === 'file' && st.url) clear(st); }, true);
    root.addEventListener('drop', function (e) { if (e.isTrusted && st.url && e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length) clear(st); }, true);
    var again = function () { placeFields(st); placeChoice(st); };
    setTimeout(again, 1500); W.addEventListener('load', again);
    var art = ''; try { art = new URLSearchParams(W.location.search).get('art') || ''; } catch (e) {}
    if (art) {
      var go = function () { if (st.started) return; st.started = 1; importUrl(st, art); };
      var api = c.api && W[c.api];
      if (api || !c.ready) setTimeout(go, 400); else { D.addEventListener(c.ready, function () { setTimeout(go, 400); }); setTimeout(go, 6000); }
    }
  }

  W.ArdorArtLink = {
    link: function (root, cur) {
      var st = stateFor(root); if (!st || !st.url) return null;
      var list = Array.isArray(cur) ? cur : [cur]; // one file, or every file on the design (DTF, table panels)
      if (st.mode === 'loaded' && st.file && list.some(function (f) { return same(f, st.file); })) return { url: st.url, loaded: true };
      if (st.mode === 'link') return { url: st.url, loaded: false }; // a later upload or drop clears the link
      return null;
    },
    emailChosen: function (root) { var st = stateFor(root); return !!(st && st.email && st.choice && !st.choice.hidden); },
    proofEmail: function (root) {
      if (W.ArdorProofLater && W.ArdorProofLater.on()) return 'Same as the checkout email'; // DSN Oct 25 2026: the email comes from checkout
      var st = stateFor(root); if (!st) return '';
      var form = formOf(st), inp = form && q(st.c.email, form), v = inp ? String(inp.value || '').trim() : '';
      if (/^\S+@\S+\.\S+$/.test(v)) return v;
      var err = form && q(st.c.err, form);
      if (err) { err.textContent = 'Please add a valid email so we can send you the proof.'; err.hidden = false; }
      try { inp && inp.focus(); } catch (e) {}
      return '';
    },
    // For designers that build their upload area or proof dialog later: call after building it.
    refresh: function (root) { states.forEach(function (st) { if (!root || st.root === root) { placeFields(st); placeChoice(st); } }); },
    importUrl: function (url, root) { var st = root ? stateFor(root) : states[0]; return st ? importUrl(st, url) : Promise.resolve({ ok: false, mode: 'error', message: 'No designer on this page.' }); },
    version: '2026-10-19'
  };

  qa('script[data-ardor-art-link]').forEach(setup);
})();
